const CHAT_API = '/api/chat.php';
const SESSION_KEY = 'yg_chat_session';

export interface ChatSessionInfo {
  sessionId: string;
  visitorName: string;
  visitorEmail: string;
}

export interface ChatMessage {
  id: number;
  session_id: string;
  sender: 'visitor' | 'agent';
  agent_name: string | null;
  message: string;
  created_at: string;
}

export interface SessionRow {
  id: string;
  visitor_name: string;
  visitor_email: string;
  visitor_phone: string;
  status: 'waiting' | 'active' | 'closed';
  assigned_to: string | null;
  close_reason: 'admin' | 'visitor' | 'timeout' | null;
  last_message: string | null;
  unread_admin: number;
  unread_visitor: number;
  updated_at: string;
  created_at: string;
}

export interface CustomerRow {
  id: number;
  name: string;
  email: string;
  phone: string;
  notes: string;
  tags: string;
  created_at: string;
  updated_at: string;
}

async function post(action: string, body: Record<string, unknown> = {}) {
  const res = await fetch(CHAT_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...body }),
  });
  const text = await res.text();
  let json: { success: boolean; message?: string; data?: unknown };
  try { json = JSON.parse(text); } catch { throw new Error('Geçersiz sunucu yanıtı'); }
  if (!json.success) throw new Error(json.message ?? 'Hata');
  return json.data;
}

async function get(params: Record<string, string>) {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${CHAT_API}?${qs}`);
  const text = await res.text();
  let json: { success: boolean; message?: string; data?: unknown };
  try { json = JSON.parse(text); } catch { throw new Error('Geçersiz sunucu yanıtı'); }
  if (!json.success) throw new Error(json.message ?? 'Hata');
  return json.data;
}

async function adminPost(action: string, body: Record<string, unknown> = {}) {
  const token = localStorage.getItem('ygApiToken') ?? '';
  const res = await fetch(CHAT_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
    body: JSON.stringify({ action, ...body }),
  });
  const text = await res.text();
  let json: { success: boolean; message?: string; data?: unknown };
  try { json = JSON.parse(text); } catch { throw new Error('Geçersiz sunucu yanıtı'); }
  if (!json.success) throw new Error(json.message ?? 'Hata');
  return json.data;
}

async function adminGet(params: Record<string, string>) {
  const token = localStorage.getItem('ygApiToken') ?? '';
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${CHAT_API}?${qs}`, {
    headers: { 'X-Admin-Token': token },
  });
  const text = await res.text();
  let json: { success: boolean; message?: string; data?: unknown };
  try { json = JSON.parse(text); } catch { throw new Error('Geçersiz sunucu yanıtı'); }
  if (!json.success) throw new Error(json.message ?? 'Hata');
  return json.data;
}

// ── Visitor API ───────────────────────────────────────────────────────────────

export async function startChatSession(name: string, email: string, phone: string): Promise<{ sessionId: string; welcomeMessage: string }> {
  const data = await post('startSession', { name, email, phone });
  return data as { sessionId: string; welcomeMessage: string };
}

export async function sendVisitorMessage(sessionId: string, message: string): Promise<void> {
  await post('sendMessage', { sessionId, message });
}

export async function pollVisitorMessages(sessionId: string, afterId: number): Promise<{ messages: ChatMessage[]; sessionStatus: string; closeReason?: string }> {
  const data = await get({ action: 'getMessages', sessionId, afterId: String(afterId) });
  return data as { messages: ChatMessage[]; sessionStatus: string; closeReason?: string };
}

export async function visitorCloseSession(sessionId: string): Promise<void> {
  await post('visitorCloseSession', { sessionId });
}

// ── Admin API ─────────────────────────────────────────────────────────────────

export async function adminGetSessions(status?: string): Promise<SessionRow[]> {
  const data = await adminGet({ action: 'getSessions', ...(status ? { status } : {}) });
  return data as SessionRow[];
}

export async function adminPollSessions(): Promise<{ sessions: SessionRow[]; totalUnread: number }> {
  const data = await adminGet({ action: 'pollSessions' });
  return data as { sessions: SessionRow[]; totalUnread: number };
}

export async function adminGetSessionMessages(sessionId: string): Promise<{ session: SessionRow; messages: ChatMessage[] }> {
  const data = await adminGet({ action: 'getSessionMessages', sessionId });
  return data as { session: SessionRow; messages: ChatMessage[] };
}

export async function adminAssignSession(sessionId: string, agentName: string): Promise<void> {
  await adminPost('assignSession', { sessionId, agentName });
}

export async function adminSendMessage(sessionId: string, message: string, agentName: string): Promise<void> {
  await adminPost('adminSendMessage', { sessionId, message, agentName });
}

export async function adminCloseSession(sessionId: string): Promise<void> {
  await adminPost('closeSession', { sessionId });
}

export async function adminGetClosedSessions(reason?: string): Promise<SessionRow[]> {
  const data = await adminGet({ action: 'getClosedSessions', ...(reason ? { reason } : {}) });
  return data as SessionRow[];
}

// ── Customer DB API ───────────────────────────────────────────────────────────

export async function adminGetCustomers(): Promise<CustomerRow[]> {
  const data = await adminGet({ action: 'getCustomers' });
  return data as CustomerRow[];
}

export async function adminAddCustomer(c: Omit<CustomerRow, 'id' | 'created_at' | 'updated_at'>): Promise<{ id: number }> {
  const data = await adminPost('addCustomer', c as unknown as Record<string, unknown>);
  return data as { id: number };
}

export async function adminUpdateCustomer(c: Omit<CustomerRow, 'created_at' | 'updated_at'>): Promise<void> {
  await adminPost('updateCustomer', c as unknown as Record<string, unknown>);
}

export async function adminDeleteCustomer(id: number): Promise<void> {
  await adminPost('deleteCustomer', { id });
}

// ── Session Storage ───────────────────────────────────────────────────────────

export function saveChatSession(info: ChatSessionInfo): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(info));
}

export function loadChatSession(): ChatSessionInfo | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearChatSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}
